import { useEffect, useState, useCallback, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { useAuth } from './useAuth';

/**
 * Options for configuring the SignalR connection
 */
export interface UseSignalRConnectionOptions {
  /**
   * Additional query string parameters to include in the connection URL
   */
  queryParams?: Record<string, string>;

  /**
   * SignalR log level
   * @default LogLevel.Warning
   */
  logLevel?: LogLevel;

  /**
   * Automatically connect when the hook mounts
   * @default true
   */
  autoConnect?: boolean;

  /**
   * Automatically reconnect when authentication state changes
   * @default true
   */
  reconnectOnAuth?: boolean;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Return type for useSignalRConnection hook
 */
export interface UseSignalRConnectionReturn {
  /**
   * The SignalR hub connection instance
   */
  connection: HubConnection | null;

  /**
   * Current connection state
   */
  connectionState: HubConnectionState;

  /**
   * Whether the connection is established
   */
  isConnected: boolean;

  /**
   * Whether the connection is in the process of connecting
   */
  isConnecting: boolean;

  /**
   * Connection error, if any
   */
  error: Error | null;

  /**
   * Manually connect to the hub
   */
  connect: () => Promise<void>;

  /**
   * Manually disconnect from the hub
   */
  disconnect: () => Promise<void>;

  /**
   * Invoke a hub method
   * @param methodName The name of the server method to invoke
   * @param args Arguments to pass to the server method
   */
  invoke: <T = void>(methodName: string, ...args: any[]) => Promise<T>;

  /**
   * Register a handler for a hub method
   * @param methodName The name of the hub method to listen for
   * @param handler The callback function to invoke when the method is called
   * @returns A function to unregister the handler
   */
  on: (methodName: string, handler: (...args: any[]) => void) => () => void;
}

/**
 * React hook for managing SignalR connections with Keycloak authentication
 *
 * @param hubUrl - The URL of the SignalR hub (e.g., "/hubs/notifications")
 * @param options - Optional configuration for the connection
 *
 * @example
 * ```tsx
 * function NotificationsComponent() {
 *   const { connection, isConnected, invoke, on } = useSignalRConnection('/hubs/notifications');
 *
 *   useEffect(() => {
 *     if (!connection) return;
 *
 *     // Register handler for receiving messages
 *     const unsubscribe = on('ReceiveMessage', (user: string, message: string) => {
 *       console.log(`${user}: ${message}`);
 *     });
 *
 *     return unsubscribe;
 *   }, [connection, on]);
 *
 *   const sendMessage = async (message: string) => {
 *     await invoke('SendMessage', message);
 *   };
 *
 *   return (
 *     <div>
 *       <p>Status: {isConnected ? 'Connected' : 'Disconnected'}</p>
 *       <button onClick={() => sendMessage('Hello!')}>Send</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useSignalRConnection(
  hubUrl: string,
  options: UseSignalRConnectionOptions = {}
): UseSignalRConnectionReturn {
  const {
    queryParams,
    logLevel = LogLevel.Warning,
    autoConnect = true,
    reconnectOnAuth = true,
    debug = false,
  } = options;

  const { user, isAuthenticated } = useAuth();
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);
  const [error, setError] = useState<Error | null>(null);
  const reconnectAttemptRef = useRef(false);

  const log = useCallback(
    (message: string, ...args: any[]) => {
      if (debug) {
        console.log(`[useSignalRConnection] ${message}`, ...args);
      }
    },
    [debug]
  );

  // Build the connection
  useEffect(() => {
    if (!hubUrl) {
      log('Hub URL is required');
      return;
    }

    log('Building SignalR connection to:', hubUrl);

    const builder = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const token = user?.access_token;
          log('Access token factory called, token length:', token?.length ?? 0);
          return token || '';
        },
        ...queryParams,
      })
      .withAutomaticReconnect()
      .configureLogging(logLevel);

    const newConnection = builder.build();

    // Set up connection state tracking
    newConnection.onclose((err) => {
      log('Connection closed', err);
      setConnectionState(HubConnectionState.Disconnected);
      if (err) {
        setError(err);
      }
    });

    newConnection.onreconnecting((err) => {
      log('Connection reconnecting', err);
      setConnectionState(HubConnectionState.Reconnecting);
      if (err) {
        setError(err);
      }
    });

    newConnection.onreconnected((connectionId) => {
      log('Connection reconnected, connection ID:', connectionId);
      setConnectionState(HubConnectionState.Connected);
      setError(null);
    });

    setConnection(newConnection);
    log('SignalR connection built');

    return () => {
      log('Cleaning up connection');
      if (newConnection.state !== HubConnectionState.Disconnected) {
        newConnection.stop().catch((err) => {
          console.error('[useSignalRConnection] Error stopping connection:', err);
        });
      }
    };
  }, [hubUrl, queryParams, logLevel, user, log]);

  // Connect function
  const connect = useCallback(async () => {
    if (!connection) {
      log('Cannot connect: connection is null');
      return;
    }

    if (connection.state === HubConnectionState.Connected) {
      log('Already connected');
      return;
    }

    if (connection.state === HubConnectionState.Connecting) {
      log('Already connecting');
      return;
    }

    try {
      log('Connecting to hub...');
      setConnectionState(HubConnectionState.Connecting);
      setError(null);
      await connection.start();
      setConnectionState(HubConnectionState.Connected);
      log('Connected successfully');
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      log('Connection failed:', error);
      setError(error);
      setConnectionState(HubConnectionState.Disconnected);
      throw error;
    }
  }, [connection, log]);

  // Disconnect function
  const disconnect = useCallback(async () => {
    if (!connection) {
      log('Cannot disconnect: connection is null');
      return;
    }

    if (connection.state === HubConnectionState.Disconnected) {
      log('Already disconnected');
      return;
    }

    try {
      log('Disconnecting from hub...');
      await connection.stop();
      setConnectionState(HubConnectionState.Disconnected);
      log('Disconnected successfully');
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      log('Disconnect failed:', error);
      setError(error);
      throw error;
    }
  }, [connection, log]);

  // Invoke hub method
  const invoke = useCallback(
    async <T = void>(methodName: string, ...args: any[]): Promise<T> => {
      if (!connection) {
        throw new Error('Cannot invoke: connection is null');
      }

      if (connection.state !== HubConnectionState.Connected) {
        throw new Error(`Cannot invoke: connection is ${connection.state}`);
      }

      log('Invoking method:', methodName, 'with args:', args);
      return connection.invoke<T>(methodName, ...args);
    },
    [connection, log]
  );

  // Register event handler
  const on = useCallback(
    (methodName: string, handler: (...args: any[]) => void): (() => void) => {
      if (!connection) {
        log('Cannot register handler: connection is null');
        return () => {};
      }

      log('Registering handler for method:', methodName);
      connection.on(methodName, handler);

      return () => {
        log('Unregistering handler for method:', methodName);
        connection.off(methodName, handler);
      };
    },
    [connection, log]
  );

  // Auto-connect when authenticated
  useEffect(() => {
    if (!connection || !autoConnect) {
      return;
    }

    if (isAuthenticated && connection.state === HubConnectionState.Disconnected) {
      log('Auto-connecting (authenticated)');
      connect().catch((err) => {
        log('Auto-connect failed:', err);
      });
    }
  }, [connection, isAuthenticated, autoConnect, connect, log]);

  // Reconnect on authentication state change
  useEffect(() => {
    if (!connection || !reconnectOnAuth || reconnectAttemptRef.current) {
      return;
    }

    const handleAuthChange = async () => {
      if (isAuthenticated && connection.state === HubConnectionState.Disconnected) {
        reconnectAttemptRef.current = true;
        log('Reconnecting on auth state change');
        try {
          await connect();
        } catch (err) {
          log('Reconnect on auth change failed:', err);
        } finally {
          reconnectAttemptRef.current = false;
        }
      } else if (!isAuthenticated && connection.state === HubConnectionState.Connected) {
        log('Disconnecting on auth state change');
        await disconnect();
      }
    };

    handleAuthChange();
  }, [isAuthenticated, connection, reconnectOnAuth, connect, disconnect, log]);

  return {
    connection,
    connectionState,
    isConnected: connectionState === HubConnectionState.Connected,
    isConnecting: connectionState === HubConnectionState.Connecting,
    error,
    connect,
    disconnect,
    invoke,
    on,
  };
}
